"use client";

import {
  createContext,
  Fragment,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
  type JSX,
} from "react";
import { DEFAULT_LOCALE, type Dict, translate } from "@repo/lib";

type TranslateFn = {
  (
    term: string,
    options?: {
      fallback?: string;
      isDesc?: boolean;
      vars?: Record<string, string>;
    },
  ): string;
  rich: (
    term: string,
    options?: {
      fallback?: string;
      isDesc?: boolean;
      components?: Record<string, React.ReactNode>;
    },
  ) => ReactNode;
};

interface I18nContextValue {
  dict: Dict;
  locale: string;
  t: TranslateFn;
}

const I18nContext = createContext<I18nContextValue | null>(null);

interface I18NProviderProps {
  children: ReactNode;
  dict: Dict;
  locale?: string;
}

export function I18NProvider({
  children,
  dict,
  locale = DEFAULT_LOCALE,
}: I18NProviderProps): JSX.Element {
  const t = useCallback<TranslateFn>(
    Object.assign(
      (
        term: string,
        options?: {
          fallback?: string;
          isDesc?: boolean;
          vars?: Record<string, string>;
        },
      ) => translate(dict, term, options),
      {
        rich: (
          term: string,
          options?: {
            fallback?: string;
            isDesc?: boolean;
            components?: Record<string, React.ReactNode>;
          },
        ): ReactNode => {
          const key = options?.isDesc ? `${term}_desc` : term;
          let template = dict[key];

          if (!template && options?.fallback) {
            return t.rich(options.fallback, options);
          }

          template = template ?? "";

          const parts = template.split(/({{.*?}})/g);
          return parts.map((part, i) => {
            const match = part.match(/{{(.*?)}}/);
            if (match) {
              const varName = match[1].trim();
              return (
                <Fragment key={i}>
                  {options?.components?.[varName] ?? null}
                </Fragment>
              );
            }
            return <Fragment key={i}>{part}</Fragment>;
          });
        },
      },
    ),
    [dict],
  );

  // Stable value: CoordinatesProvider re-provides the dict for the whole map tree.
  const value = useMemo(() => ({ dict, locale, t }), [dict, locale, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

// Hook to access i18n context
export const useI18n = (): I18nContextValue => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useI18n must be used within an I18NProvider");
  }
  return context;
};

// Hook to access translation function
export const useT = (): TranslateFn => useI18n().t;

// Translate where an I18NProvider may be absent (shared chrome like the game
// switcher also renders in the Overwolf/desktop app shells): falls back to the
// `fallback` text, else the key.
export const useOptionalT = (): ((
  term: string,
  options?: { fallback?: string },
) => string) => {
  const context = useContext(I18nContext);
  return context?.t ?? ((term, options) => options?.fallback ?? term);
};

// Hook to access locale
export const useLocale = (): string => useI18n().locale;

// Locale where an I18NProvider may be absent (Overwolf / desktop app shells):
// null there. Used to open www.th.gl pages in the visitor's language (wwwUrl).
export const useOptionalLocale = (): string | null =>
  useContext(I18nContext)?.locale ?? null;
